/**
 * Alternate names — the comma-separated `alt_names` / `alternate_names`
 * columns on suppliers, hotels, agents and arrival points.
 *
 * Every reader (the log importer, the supplier recap, the hotel mailers,
 * AgentResolver) splits the column on commas; AgentResolver also accepts
 * `|`, which a few agent rows use. Editors show the list as tags and write it
 * back through `joinAliases`, which is the server's `AliasColumn.Join`
 * twin: trimmed, inner spaces collapsed, repeats dropped case-insensitively,
 * joined with ", ".
 */

const SEPARATORS = /[,|]+/;

/** Case-insensitive, whitespace-collapsed — how the resolvers compare names. */
export function aliasKey(s: string | null | undefined): string {
  return squash(s).toLowerCase();
}

function squash(s: string | null | undefined): string {
  return (s ?? '').trim().replace(/\s+/g, ' ');
}

/** The column (or a pasted blob) as a list, repeats removed, first spelling kept. */
export function parseAliases(raw: string | null | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of (raw ?? '').split(SEPARATORS)) {
    const alias = squash(part);
    if (!alias) continue;
    const key = alias.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(alias);
  }
  return out;
}

/**
 * Pasted text → tags. Wider than `parseAliases`: a list copied from a
 * spreadsheet or a message arrives one-per-line or `;`-separated.
 */
export function splitPasted(text: string): string[] {
  return parseAliases(text.replace(/[\n\r;\t]+/g, ','));
}

/**
 * The list as the column stores it. Returns `''` (not null) for an empty
 * list: the save endpoints read `""` as "the user removed every alias" and
 * `null` as "not sent", and only the first one clears the column.
 */
export function joinAliases(list: readonly string[]): string {
  return parseAliases(list.join(',')).join(', ');
}
